import { NoDataComponent, CustomButton, CustomTooltip } from '@/shared/ui/components';
import { ArtifactIcon, CheckCircleIcon, InfoIcon } from '@/shared/ui/assets';
import {
  ARTIFACT_REPORT_HOVER_TEXT,
  DOUBLE_HYPHEN_BULLET,
  MESSAGE_ARTIFACT_REPORT,
  REPORT_REVIEWED_HOVER_TEXT,
} from '@/shared/config';
import { dateToDuration, dateToTimeSince } from '@/shared/lib';
import classNames from 'classnames';
import _ from 'lodash';
import { forwardRef, memo } from 'react';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import InboxItemSkeleton from '../inboxItemSkeleton/inboxItemSkeleton';
import InboxSearchComponent from '../../inboxSearchComponent/inboxSearchComponent';
import '../style.scss';
import { generateLabelsInstantReport } from '../../../lib/instantInboxList';
import { useInstantInboxList } from './useInstantInboxList';
import type { InstantInboxListProps } from './instantInboxList.types';

/**
 * Instant inbox list of unreviewed reports. Renders InboxSearchComponent with the priority and status filters hidden, pages state.inboxes by sizePerPage into cards showing patient name, generateLabelsInstantReport labels, artifact and reviewed icons, patient ID, physician, date of service, study type, findings and a time-since stamp, falling back to skeleton items while loading or NoDataComponent when a search matches nothing.
 *
 * @param props - List props including the active tab flag, facility selection and drawer toggle.
 * @param ref - Ref exposing the list imperative handle.
 * @returns The instant inbox list element, or false while the tab is inactive and unloaded.
 */
const InstantInboxList = forwardRef((props: InstantInboxListProps, ref) => {
  const { state, filterObject, sizePerPage, onChangeSearchComponent, navigatePage, onClickReport } =
    useInstantInboxList(props, ref);

  return (
    (props.isActive || state.isLoadedData) && (
      <div className="inbox-list">
        {(props.isActive || state.isLoadedData) && (
          <InboxSearchComponent
            isLoadedData={state.isLoadedData}
            inboxes={state.inboxes}
            pageIndex={state.pageIndex}
            isEndOfData={state.isEndOfData}
            totalInboxCount={props.totalInboxCount}
            onChangeSearchComponent={onChangeSearchComponent}
            navigatePage={navigatePage}
            reportType="UNREVIEWED"
            searchGoto={props.searchGoto}
            updateSearchGoto={props.updateSearchGoto}
            selectedFacility={props.selectedFacility}
            selectedTypeSeePatients={props.selectedTypeSeePatients}
            removePrioritySelect
            hiddenFilterStatus
          />
        )}
        {props.isActive && (
          <div
            id="inbox-list-body"
            className={classNames('inbox-list__body', !state.isLoadedData ? '--skeleton' : '')}
          >
            {state.inboxes.length || (!state.inboxes.length && !state.isLoadedData) ? (
              _.map(
                state.isLoadedData
                  ? state.inboxes.slice(
                      state.pageIndex * sizePerPage.current,
                      (state.pageIndex + 1) * sizePerPage.current,
                    )
                  : state.skeletonInboxes,
                (report, index) =>
                  state.isLoadedData ? (
                    <div
                      className="inbox-item"
                      onClick={() => {
                        onClickReport(report);
                      }}
                    >
                      <div className="inbox-item__top-container">
                        <div className="left-content-container">
                          <span className="patient-name">{report.patientName}</span>
                        </div>

                        <div className="right-content-container">
                          <div className="info-container">
                            {_.map(
                              generateLabelsInstantReport(report, props.toggleDisplayDrawer),
                              (label, index) =>
                                label.isTextButton ? (
                                  <CustomButton
                                    id={`view-details-btn-${report.id}`}
                                    type="secondary"
                                    className="study-id-button --margin"
                                    tooltipTitle={t`View details`}
                                    tooltipPlacement="bottom"
                                    tooltipClassName="tooltip-study-id"
                                    icon={<ReactSVG src={InfoIcon} />}
                                    isRightIcon
                                    onClick={label.handleClick}
                                  >
                                    {label.text}
                                  </CustomButton>
                                ) : (
                                  <div
                                    key={index}
                                    className={classNames('label', '--margin', label.className)}
                                  >
                                    {label.text}
                                  </div>
                                ),
                            )}
                          </div>

                          <div className="items-center">
                            {report.isArtifactReport && (
                              <CustomTooltip
                                placement="bottomRight"
                                title={ARTIFACT_REPORT_HOVER_TEXT}
                              >
                                <img src={ArtifactIcon} alt="Artifact Icon" />
                              </CustomTooltip>
                            )}
                            {report.isReviewed && (
                              <CustomTooltip
                                placement="bottomRight"
                                title={REPORT_REVIEWED_HOVER_TEXT}
                              >
                                <img src={CheckCircleIcon} alt="Check Circle Icon" />
                              </CustomTooltip>
                            )}
                          </div>
                        </div>
                      </div>

                      {state.isShowFacilityName && (
                        <div className="inbox-item__facility-container">{report.facilityName}</div>
                      )}

                      <div className="inbox-item__bottom-container">
                        <div className="left-container">
                          <div className="info-item patient-id">
                            <div className="title">{`${t`Patient ID`}:`}</div>
                            <div className="content">
                              <CustomButton
                                id="drawer-btn"
                                className="content button-content"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  props.toggleDisplayDrawer(report, t`Patient information`);
                                }}
                                type="tertiary"
                                size="small"
                              >
                                {report.patientId}
                              </CustomButton>
                            </div>
                          </div>
                          <div className="info-item">
                            <div className="title">{`${t`Physician`}:`}</div>
                            <div className="content">{report.interprettingPhysicianName}</div>
                          </div>
                          <div className="info-item">
                            <div className="title">{`${t`Date of service`}:`}</div>
                            <div className="content">{report.dateOfService}</div>
                          </div>
                          <div className="info-item">
                            <div className="title">{`${t`Study type`}:`}</div>
                            <div className="content">{report.studyType}</div>
                          </div>
                        </div>

                        <div className="right-container">
                          <div className="border-left-container">
                            <div className="technician-comments__title">{t`Findings`}</div>
                            <CustomTooltip
                              classNames={{ root: 'technician-comments__tooltip' }}
                              placement="bottomLeft"
                              mouseLeaveDelay={0}
                              mouseEnterDelay={0}
                              arrow={false}
                              title={
                                report.isArtifactReport
                                  ? ''
                                  : (report.physicianInterpretation ?? report.technicianComments)
                              }
                            >
                              <div
                                id={`technician-comments-${report.id}`}
                                className="technician-comments__content"
                              >
                                {report.isArtifactReport
                                  ? MESSAGE_ARTIFACT_REPORT
                                  : (report.physicianInterpretation ?? report.technicianComments) ||
                                    DOUBLE_HYPHEN_BULLET}
                              </div>
                            </CustomTooltip>
                          </div>

                          <div className="time justify-end">
                            <div>{dateToTimeSince(dateToDuration(report.lastModified))}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <InboxItemSkeleton key={index} />
                  ),
              )
            ) : (
              <NoDataComponent
                isSearching={
                  !!filterObject.current.patientName ||
                  !!filterObject.current.studyFid ||
                  !!filterObject.current.patientId ||
                  !!filterObject.current.physicianId
                }
              />
            )}
          </div>
        )}
      </div>
    )
  );
});

export default memo(InstantInboxList);
